import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { delivered, handled, leftOn, nextStop, type Route, type Stop } from '@day';
import { useStore } from '../app/store';
import { backFromStop, backToDay, besideMap, discardPlan, discardShare, openCourier, openPerson, openRoute, openShare, openStop, planWith, toggleSec, ui, go, type UI } from '../app/ui';
import { ago, and, courierFirst, dayBefore, dayOf, dayTime, dayWords, firstOf, inMinutes, length, money, plural, reasonWords, streetOf, time, two, vehicleWords } from '../app/words';
import { keyOf, routeTitle, type Look, type Model, type Person } from '../data/model';
import { Av, EmptyHelp, Facts, More, Segs, TPill, nearly } from '../ui/bits';
import { Icon } from '../ui/Icon';
import { toast } from '../ui/Overlays';
import { StopRecord } from './StopBody';
import { ClosestView, PlanView } from './Plan';
import { ShareView } from './Share';
import * as act from '../app/actions';
import { openDialog } from '../app/dialogs';
import { newRouteWith } from '../app/menus';
import { DayTable, RouteTable } from './LiveTables';
import { MapPane } from '../map/MapPane';
import { RoutesList } from './Routes';
import { printRoute } from './RouteSheet';

/** A stop opened from a route keeps that route's stops in the list; one opened from today keeps today. */
const backTip = () => (ui.get().page === 'routes' ? 'Back to the routes · Esc' : 'Back to today · Esc');
const inRoute = (s: UI) => !!s.route && (s.view === 'route' || (s.view === 'stop' && s.back === 'route'));

/**
 * Live: the list beside the map, with a stop's record between them while one is open; or (List) the same day as tables.
 * Routes is the same frame with its own list, and always the map.
 */
export function Live({ m }: { m: Model }) {
  const s = useStore(ui);
  const map = besideMap(s);
  const stop = s.view === 'stop' && s.stop ? m.stop(s.stop) : null;
  const listing = inRoute(s) ? `route:${s.route}` : s.view === 'person' ? `person:${s.person}` : 'day';
  const list = useRef<HTMLElement>(null);
  const dayAt = useRef(0);
  // Each list starts at its top; going back finds today where it was left.
  useLayoutEffect(() => {
    if (list.current) list.current.scrollTop = listing === 'day' ? dayAt.current : 0;
  }, [listing, map, s.page]);
  return (
    <section className={`page on${!map ? ' nomap' : ''}`} id="p-live">
      {map ? (
        <>
          <aside
            className="list"
            id="liveList"
            ref={list}
            onScroll={(e) => {
              if (listing === 'day') dayAt.current = e.currentTarget.scrollTop;
            }}
          >
            <LiveList m={m} s={s} />
          </aside>
          {stop && (
            <aside className="pane" aria-label="Stop">
              <StopPane m={m} s={stop} />
            </aside>
          )}
          <MapPane m={m} />
        </>
      ) : (
        <div className="wide" id="liveWide" style={{ display: 'block' }}>
          <div className="tpage">
            {s.view === 'route' && m.route(s.route ?? '') ? (
              <RouteTable m={m} r={m.route(s.route!)!} />
            ) : s.view === 'person' && m.person(s.person ?? '') ? (
              <div className="card">
                <PersonView m={m} p={m.person(s.person!)!} />
              </div>
            ) : (
              <DayTable m={m} />
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function LiveList({ m, s }: { m: Model; s: UI }) {
  const body = <LiveBody m={m} s={s} />;
  // Stops being shared out stay when the owner looks elsewhere: carry on, or put them away.
  const shareBar = s.view !== 'share' && s.share?.ids.length ? (
    <div className="brow planbar">
      <Icon name="arrowsSplit" />
      <div className="grow">
        <div className="ink">Sharing out · {plural(s.share.ids.length, 'stop')}</div>
        <div className="cap">Not sent yet</div>
      </div>
      <button className="link" onClick={() => discardShare((msg, undo) => toast(msg, { undo: () => void undo() }))}>
        Discard
      </button>
      <button className="link" onClick={() => openShare([], [])}>
        Carry on
        <Icon name="caretRight" size="sm" />
      </button>
    </div>
  ) : null;
  // A route being planned stays when the owner looks elsewhere: carry on with it, or discard it.
  if (s.view === 'plan' || !s.plan?.stops.length)
    return (
      <>
        {shareBar}
        {body}
      </>
    );
  const to = s.plan.to ? m.person(s.plan.to) : null;
  return (
    <>
      {shareBar}
      <div className="brow planbar">
        <Icon name="path" />
        <div className="grow">
          <div className="ink">New route · {plural(s.plan.stops.length, 'stop')}</div>
          <div className="cap">{to ? `For ${to.first}, not sent yet` : 'Not sent yet'}</div>
        </div>
        <button className="link" onClick={() => discardPlan((msg, undo) => toast(msg, { undo: () => void undo() }))}>
          Discard
        </button>
        <button className="link" onClick={() => planWith()}>
          Carry on
          <Icon name="caretRight" size="sm" />
        </button>
      </div>
      {body}
    </>
  );
}

function LiveBody({ m, s }: { m: Model; s: UI }) {
  if (s.view === 'plan' && s.plan) return <PlanView m={m} />;
  if (s.view === 'share' && s.share) return <ShareView m={m} />;
  if (s.view === 'closest' && s.closest) return <ClosestView m={m} />;
  if (inRoute(s)) {
    const r = m.route(s.route!);
    if (r) return <RouteView m={m} r={r} />;
  }
  if (s.view === 'person' && s.person) {
    const p = m.person(s.person);
    if (p) return <PersonView m={m} p={p} />;
  }
  return s.page === 'routes' ? <RoutesList m={m} /> : <DayView m={m} />;
}

/** The route a courier's name opens on Live: running, else waiting, else today's last. */
export const routeOfPerson = (m: Model) => (uid: string) => {
  const p = m.person(uid);
  if (!p?.day) return null;
  return p.day.running?.id ?? p.day.waiting[0]?.id ?? p.day.finished[0]?.id ?? p.day.leftOpen?.id ?? null;
};

// ── Today ─────────────────────────────────────────────────────────────────

/** What the numbers at the top of today are made of, on Live's list and above List's tables. */
function dayFacts(m: Model) {
  const d = m.day;
  const running = m.people.filter((p) => p.day?.running);
  const failed = m.stops.filter((s) => s.state === 'failed' && m.route(s.routeId)?.state !== 'waiting');
  const holds = m.people.filter((p) => p.balance > 0);
  const owed = m.people.filter((p) => p.balance < 0);
  return {
    total: d.delivered + d.notDelivered + d.toGo || 1,
    running,
    doneBy: Math.max(0, ...running.map((p) => p.day?.finishAt ?? 0)),
    anyLate: m.stops.some((s) => !handled(s) && s.eta != null && s.deliverBy != null && s.eta > s.deliverBy),
    failed,
    why: Object.entries(failed.reduce<Record<string, number>>((a, s) => ((a[reasonWords(s.reason)] = (a[reasonWords(s.reason)] ?? 0) + 1), a), {}))
      .map(([w, k]) => `${w} ${k}`)
      .join(' · '),
    holds,
    owed,
    moneyTitle: m.handInTotal ? `${money(m.handInTotal, m.currency)} to hand in` : `You owe ${money(m.owedTotal, m.currency)}`,
    moneyLine: [
      ...holds.map((p) => `${p.first} ${money(p.balance, m.currency)}`),
      ...owed.map((p) => `you owe ${p.first} ${money(-p.balance, m.currency)}`),
      // Not in the balance until the route ends (settling-up.html).
      ...(m.people.some((p) => p.onRoute > 0) ? [`${money(m.people.reduce((n, p) => n + Math.max(0, p.onRoute), 0), m.currency)} more on routes still running`] : []),
    ].join(' · '),
    toGoTip: `On routes that have started.${d.waitingStops ? ` ${plural(d.waitingStops, 'stop')} more wait on routes not started yet.` : ''}`,
    seeFailed: () => {
      go('stops');
      ui.set({ tab: 'failed', courier: null, q: '', sel: new Set() });
    },
    seeMoney: () => {
      const p = holds[0] ?? owed[0];
      go('couriers');
      if (p) ui.set({ rec: { type: 'courier', id: p.uid } });
    },
  };
}

const againstYesterday = (up: number | null) =>
  up == null ? null : up > 0 ? `${up} more delivered than yesterday by now` : up < 0 ? `${-up} fewer delivered than yesterday by now` : 'The same as yesterday by now';

/** The numbers at the top of today, for List's page: delivered, not delivered, to go; who's out, and what's behind them. */
export function Summary({ m }: { m: Model }) {
  const d = m.day;
  const f = dayFacts(m);
  const n = m.looks.length;
  const head = n === 1 ? '1 needs a look' : n ? `${n} need a look` : f.anyLate ? 'Nothing needs a look' : 'All on time';
  const yesterday = useYesterdayByNow(m);
  const against = againstYesterday(yesterday == null ? null : d.delivered - yesterday);
  return (
    <>
      <div className="title">{head}</div>
      <div className="cap" style={{ marginTop: 3 }}>
        {f.running.length ? `${and(f.running.map((p) => p.first))} out · done by about ${time(f.doneBy || null, m.zone)}` : 'Nobody on a route now'}
      </div>
      <div className="big3">
        <div>
          <div className="v">{d.delivered}</div>
          <div className="l">delivered</div>
        </div>
        <div>
          <div className={`v${d.notDelivered ? ' neg' : ''}`}>{d.notDelivered}</div>
          <div className="l">not delivered</div>
        </div>
        <div>
          <div className="v">{d.toGo}</div>
          <div className="l">
            <span className="dotted" data-tip={f.toGoTip}>
              to go
            </span>
          </div>
        </div>
      </div>
      <div className="bar">
        <i style={{ width: `${(d.delivered / f.total) * 100}%`, background: 'var(--positive)' }} />
        <i style={{ width: `${(d.notDelivered / f.total) * 100}%`, background: 'var(--negative)' }} />
      </div>
      {against && (
        <div className="cap" style={{ marginTop: 8 }}>
          {against}
        </div>
      )}
      <div className="brk">
        {f.failed.length > 0 && (
          <div className="brow">
            <Icon name="xCircle" />
            <div className="grow">
              <div className="ink">{plural(f.failed.length, 'stop')} not delivered</div>
              <div className="cap">{f.why}</div>
            </div>
            <button className="link" onClick={f.seeFailed}>
              See them
              <Icon name="caretRight" size="sm" />
            </button>
          </div>
        )}
        {(f.holds.length > 0 || f.owed.length > 0) && (
          <div className="brow">
            <Icon name="money" />
            <div className="grow">
              <div className="ink">{f.moneyTitle}</div>
              <div className="cap">{f.moneyLine}</div>
            </div>
            <button className="link" onClick={f.seeMoney}>
              Hand-ins
              <Icon name="caretRight" size="sm" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/** The top of today on Live's list: the date and the three numbers, so the routes are on the first screen. */
function DayHead({ m }: { m: Model }) {
  const d = m.day;
  const f = dayFacts(m);
  const yesterday = useYesterdayByNow(m);
  const note = [m.looks.length ? null : f.anyLate ? 'Nothing needs a look' : 'All on time', againstYesterday(yesterday == null ? null : d.delivered - yesterday)].filter(Boolean).join(' · ');
  return (
    <>
      <div className="dayt">
        <div className="title">{dayWords(m.today)}</div>
        <span className="cap">{f.running.length ? `done by about ${time(f.doneBy || null, m.zone)}` : 'Nobody on a route now'}</span>
      </div>
      <div className="nums">
        <span>
          <b>{d.delivered}</b>delivered
        </span>
        {d.notDelivered > 0 ? (
          <button data-tip={`${f.why} · see them`} onClick={f.seeFailed}>
            <b className="neg">{d.notDelivered}</b>not delivered
          </button>
        ) : (
          <span>
            <b>0</b>not delivered
          </span>
        )}
        <span>
          <b>{d.toGo}</b>
          <span className="dotted" data-tip={f.toGoTip}>
            to go
          </span>
        </span>
      </div>
      <div className="bar">
        <i style={{ width: `${(d.delivered / f.total) * 100}%`, background: 'var(--positive)' }} />
        <i style={{ width: `${(d.notDelivered / f.total) * 100}%`, background: 'var(--negative)' }} />
      </div>
      {note && <div className="cap note">{note}</div>}
    </>
  );
}

/** Delivered yesterday by this time of day: from the past days read for Reports. */
function useYesterdayByNow(m: Model): number | null {
  const h = m.history;
  return useMemo(() => {
    if (!h) return null;
    const byNow = m.now - 24 * 60 * 60 * 1000;
    let n = 0;
    let worked = false;
    for (const r of h)
      for (const s of r.stops) {
        if (s.at == null || s.at < m.yesterdayStart || s.at >= m.todayStart) continue;
        worked = true;
        if (s.state === 'done' && s.at <= byNow) n++;
      }
    // A day off yesterday says nothing about today.
    return worked ? n : null;
  }, [h, m.now, m.yesterdayStart, m.todayStart]);
}

const tone = (x: Look) => (x.act ? 'var(--negative)' : 'var(--caution)');

/** The actions under a line in Needs a look (control-room-web.html `lookActs`); a call only with a number to call. */
function lookActs(x: Look): { icon: string; label: string; tel?: string; run?: () => void; menu?: string }[] {
  const call = (label: string, tel: string | undefined) => (tel ? [{ icon: 'phone', label, tel }] : []);
  const callCustomer = call('Call customer', x.stop?.phone);
  const callCourier = call(`Call ${x.person?.first ?? 'the courier'}`, x.person?.courier.phone);
  const clear = x.clearable ? [{ icon: 'check', label: 'Clear', run: () => ui.set({ noting: x.id }) }] : [];
  const sid = x.id.slice(x.id.indexOf(':') + 1);
  switch (x.kind) {
    case 'late':
      return [...callCustomer, ...(x.stop?.trackingCode ? [{ icon: 'link', label: 'Send link', run: () => act.sendLink(x.stop!) }] : []), ...clear];
    case 'anotherDay':
      return [...(x.stop ? [{ icon: 'calendarBlank', label: 'Plan it on a new route', run: () => newRouteWith([x.stop!]) }] : []), ...callCustomer, ...clear];
    case 'notAttempted':
      return [...(x.route ? [{ icon: 'path', label: 'Send them on a new route', run: () => newRouteWith(x.route!.stops.filter((s) => s.state === 'left')) }] : []), ...callCourier, ...clear];
    case 'unpaid':
    case 'quiet':
    case 'failedInARow':
    case 'farFromAddress':
      return [...callCourier, ...clear];
    case 'stillOpen':
      return [...callCourier, ...(x.route ? [{ icon: 'bell', label: `Remind ${x.person?.first ?? 'them'}`, run: () => act.remindCourier(x.route!.id) }] : []), ...clear];
    case 'declined':
    case 'stale':
      return x.route
        ? [...callCourier, { icon: 'arrowsClockwise', label: 'Give it to someone else', menu: `give:${x.route.id}` }, { icon: 'arrowUUpLeft', label: 'Take it back', run: () => act.takeBackRoute(x.route!.id) }]
        : callCourier;
    case 'stopDeclined':
    case 'stopStale':
      return [...callCourier, { icon: 'arrowUUpLeft', label: 'Take it back', run: () => act.takeBackSentStop(sid) }];
    case 'handIn':
      return x.person ? [{ icon: 'check', label: 'Confirm what came in', run: () => openDialog({ kind: 'handIn', riderId: x.person!.uid, handInId: sid }) }, ...callCourier] : callCourier;
    default:
      return callCourier;
  }
}

/** "Clear with a note": the note is optional, and the alert leaves the list for today (a route still open, for good). */
function NoteField({ x }: { x: Look }) {
  const [note, setNote] = useState('');
  return (
    <div className="acts notefield" onClick={(e) => e.stopPropagation()}>
      <label className="field grow">
        <Icon name="notePencil" size="sm" />
        <input
          autoFocus
          placeholder="What happened? (optional)"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') act.clearLook(x, note);
            if (e.key === 'Escape') {
              e.stopPropagation();
              ui.set({ noting: null });
            }
          }}
          aria-label="A note (optional)"
        />
      </label>
      <button className="mini strong" onClick={() => act.clearLook(x, note)}>
        Clear
      </button>
      <button className="mini" onClick={() => ui.set({ noting: null })}>
        Cancel
      </button>
    </div>
  );
}

function LookActs({ x, more }: { x: Look; more?: ReactNode }) {
  if (ui.get().noting === x.id) return <NoteField x={x} />;
  return (
    <div className="acts" onClick={(e) => e.stopPropagation()}>
      {lookActs(x).map((a) =>
        a.tel ? (
          <a key={a.label} className="mini" href={`tel:${a.tel.replace(/\s/g, '')}`}>
            <Icon name={a.icon} size="sm" />
            {a.label}
          </a>
        ) : a.menu ? (
          <button key={a.label} className="mini" data-menu={a.menu}>
            <Icon name={a.icon} size="sm" />
            {a.label}
          </button>
        ) : (
          <button key={a.label} className="mini" onClick={a.run}>
            <Icon name={a.icon} size="sm" />
            {a.label}
          </button>
        ),
      )}
      {more}
    </div>
  );
}

/** A courier's stops not delivered this week, to read: what to do with each is decided on the phone for now. */
function Missed({ lines }: { lines: string[] }) {
  const shown = lines.slice(0, 12);
  return (
    <ul className="missed cap">
      {shown.map((l, i) => (
        <li key={i}>{l}</li>
      ))}
      {lines.length > shown.length && <li>and {lines.length - shown.length} more</li>}
    </ul>
  );
}

/** A line in Needs a look with its actions showing: List's page, where there's room. */
export function LookRow({ x }: { x: Look }) {
  const open = () => {
    if (x.stop) return openStop(keyOf(x.stop), 'day');
    if (x.route) return openRoute(x.route.id);
    if (x.person) return openCourier(x.person.uid);
  };
  return (
    <div className="item" data-ctx={`look:${x.id}`} onClick={open}>
      <span className="kdisc" style={{ '--tone': tone(x) } as React.CSSProperties}>
        <Icon name={x.icon} />
        <Av p={x.person} size="xs" />
      </span>
      <div className="grow">
        <div className="eyebrow" style={{ color: tone(x) }}>
          {x.act ? 'Needs action' : 'Watch'}
        </div>
        <b style={{ display: 'block', marginTop: 2 }}>{x.title}</b>
        <div className="cap">{x.detail}</div>
        {x.missed && <Missed lines={x.missed} />}
        <LookActs x={x} />
      </div>
      <More menu={`look:${x.id}`} />
    </div>
  );
}

/** A line in Needs a look on Live's list: the words alone until it's opened, and its stop's record opens beside it. */
function LookLine({ x, open, onOpen }: { x: Look; open: boolean; onOpen: (id: string | null) => void }) {
  const s = ui.get();
  const picked = !!x.stop && s.view === 'stop' && s.stop === keyOf(x.stop);
  return (
    <div
      className={`item look${open || picked ? ' hot' : ''}`}
      data-ctx={`look:${x.id}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={() => {
        onOpen(open ? null : x.id);
        if (x.stop && !picked) openStop(keyOf(x.stop), 'day');
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        onOpen(open ? null : x.id);
        if (x.stop && !picked) openStop(keyOf(x.stop), 'day');
      }}
    >
      <span className="kdisc" style={{ '--tone': tone(x) } as React.CSSProperties}>
        <Icon name={x.icon} size="sm" />
        <Av p={x.person} size="xs" />
      </span>
      <div className="grow">
        <div>{x.title}</div>
        {(open || ui.get().noting === x.id) && (
          <>
            <div className="cap">{x.detail}</div>
            {x.missed && <Missed lines={x.missed} />}
            <LookActs
              x={x}
              more={
                !x.stop && x.route ? (
                  <button className="mini" onClick={() => openRoute(x.route!.id)}>
                    <Icon name="path" size="sm" />
                    See the route
                  </button>
                ) : !x.stop && x.person ? (
                  <button className="mini" onClick={() => openCourier(x.person!.uid)}>
                    <Icon name="users" size="sm" />
                    See {x.person.first}
                  </button>
                ) : null
              }
            />
          </>
        )}
      </div>
      <More menu={`look:${x.id}`} />
    </div>
  );
}

/** A section of today's list: folds away, and its routes can be hidden from the map. */
export function Sec({ id, label, n, eye, closed }: { id: string; label: string; n?: number | string; eye?: { ids: string[]; hidden: boolean }; closed: boolean }) {
  return (
    <div className="lsec">
      <button className="sech" aria-expanded={!closed} onClick={() => toggleSec(id)}>
        <Icon name={closed ? 'caretRight' : 'caretDown'} size="sm" />
        <span className="eyebrow">{label}</span>
        {n !== undefined && <span className="cnt">{n}</span>}
      </button>
      {eye && (
        <button
          className="iconbtn sm eye"
          data-tip={eye.hidden ? 'Show on the map' : 'Hide from the map'}
          aria-label={eye.hidden ? 'Show on the map' : 'Hide from the map'}
          onClick={() => {
            const all = eye.ids.every((id) => ui.get().hidden.has(id));
            ui.set((s) => {
              const hidden = new Set(s.hidden);
              for (const id of eye.ids) {
                if (all) hidden.delete(id);
                else hidden.add(id);
              }
              return { hidden };
            });
          }}
        >
          <Icon name={eye.hidden ? 'eyeSlash' : 'eye'} />
        </button>
      )}
    </div>
  );
}

export const eyeOf = (ids: string[], hidden: Set<string>) => ({ ids, hidden: ids.length > 0 && ids.every((id) => hidden.has(id)) });

/** A waiting route's line: "5 stops · sent 12:00 PM · seen 12:10 PM". */
export function sentLine(m: Model, r: Route): string {
  const name = m.person(r.riderId)?.first ?? 'them';
  if (m.sending.has(r.id)) return `${plural(r.stops.length, 'stop')} · sending`;
  const seen = r.orderChangedAt != null ? `order changed by ${name}` : r.seenAt != null ? `seen ${time(r.seenAt, m.zone)}` : 'not seen yet';
  if (r.state === 'declined') return `${plural(r.stops.length, 'stop')} · ${name} can’t take it`;
  const est = m.ifNow.routes.get(r.id);
  return [plural(r.stops.length, 'stop'), est ? `done about ${time(est.finish, m.zone)} if it leaves now` : null, r.sentAt != null ? `sent ${time(r.sentAt, m.zone)}` : null, seen].filter(Boolean).join(' · ');
}

/** A route at a glance: when it finishes or finished, the stops done, how long it is. */
function RouteMeta({ m, r }: { m: Model; r: Route }) {
  const p = m.person(r.riderId);
  const running = r.state === 'running';
  const at = running ? (p?.day?.running?.id === r.id ? p.day.finishAt : null) : (r.completedAt ?? r.endedAt);
  const n = running ? r.stops.filter(handled).length : delivered(r);
  return (
    <div className="meta">
      {at != null && <span data-tip={running ? 'An estimate, from where the courier is now' : undefined}>{`${running ? 'Finishing' : r.state === 'ended' ? 'Ended' : 'Finished'} ${time(at, m.zone)}`}</span>}
      <span className="dotb" data-tip={`${n} of ${plural(r.stops.length, 'stop')} ${running ? 'handled' : 'delivered'}`}>
        <Icon name="mapPin" size="sm" />
        {n}/{r.stops.length}
      </span>
      {r.km ? (
        <span className="dotb" data-tip="The route’s length">
          <Icon name="path" size="sm" />
          {length(r.km * 1000)}
        </span>
      ) : null}
    </div>
  );
}

const rowStyle = (p: Person | null, hidden = false) => ({ '--c': p?.hex ?? '#5E594F', '--on': p?.on ?? '#fff', ...(hidden ? { opacity: 0.5 } : {}) }) as React.CSSProperties;

/** The courier and the route, in the courier's colour: the same colour as their line on the map. */
function RouteHead({ m, r, p, done, sub }: { m: Model; r: Route; p: Person | null; done?: boolean; sub: ReactNode }) {
  return (
    <div className="who">
      <span className="avw">
        <Av p={p} />
        {done && (
          <span className="okb">
            <Icon name="check" />
          </span>
        )}
      </span>
      <div className="grow">
        <div className="nm">
          {p?.first ?? 'A courier'} <span className="rt">· {routeTitle(r, m.zone)}</span>
        </div>
        {sub}
      </div>
      <More menu={`route:${r.id}`} />
    </div>
  );
}

function SentRow({ m, r }: { m: Model; r: Route }) {
  const p = m.person(r.riderId);
  const s = ui.get();
  return (
    <div className={`rrow${s.view === 'stop' && s.route === r.id ? ' on' : ''}`} data-ctx={`route:${r.id}`} style={rowStyle(p, s.hidden.has(r.id))} onClick={() => openRoute(r.id)}>
      <RouteHead m={m} r={r} p={p} sub={<div className="meta wrap">{sentLine(m, r)}</div>} />
    </div>
  );
}

function RunRow({ m, p }: { m: Model; p: Person }) {
  const s = ui.get();
  const r = p.day!.running!;
  const cur = nextStop(r);
  const failed = r.stops.filter((x) => x.state === 'failed');
  const unpaid = r.stops.filter((x) => x.paidHow === 'unpaid');
  const quiet = p.day?.pausedForNight ? 'Position off for the night' : p.day?.quiet ? 'No position for a while' : null;
  return (
    <div className={`rrow${s.view === 'stop' && s.route === r.id ? ' on' : ''}`} data-ctx={`route:${r.id}`} style={rowStyle(p, s.hidden.has(r.id))} onClick={() => openRoute(r.id)}>
      <RouteHead m={m} r={r} p={p} sub={<RouteMeta m={m} r={r} />} />
      <Segs stops={r.stops} />
      {cur ? (
        <div
          className={`nextrow${s.view === 'stop' && s.stop === keyOf(cur) ? ' hot' : ''}`}
          data-ctx={`stop:${keyOf(cur)}`}
          onClick={(e) => {
            e.stopPropagation();
            openStop(keyOf(cur), 'day');
          }}
        >
          <span className="n">{two(cur.n)}</span>
          <div style={{ minWidth: 0 }}>
            <div className="a ellipsis">{streetOf(cur)}</div>
            <div className="s ellipsis">{[cur.area, cur.name].filter(Boolean).join(' · ') || ' '}</div>
          </div>
          <TPill s={cur} zone={m.zone} short />
        </div>
      ) : (
        <div className="cap after">{r.betweenRuns ? 'Back to load the next run' : r.returning ? 'Every stop handled; on the way back' : 'Every stop handled'}</div>
      )}
      {(failed.length > 0 || unpaid.length > 0 || quiet) && (
        <div className="chips">
          {quiet && (
            <span className="chip cau">
              <Icon name="warningCircle" size="sm" />
              {quiet}
            </span>
          )}
          {failed.length > 0 && (
            <button
              className="chip neg"
              onClick={(e) => {
                e.stopPropagation();
                if (failed.length === 1) openStop(keyOf(failed[0]), 'day');
                else openRoute(r.id);
              }}
            >
              <Icon name="xCircle" size="sm" />
              {failed.length} not delivered
            </button>
          )}
          {unpaid.map((x) => (
            <button
              key={x.id}
              className="chip neg"
              onClick={(e) => {
                e.stopPropagation();
                openStop(keyOf(x), 'day');
              }}
            >
              <Icon name="money" size="sm" />
              {money(x.collectOrder + x.collectFee, x.currency ?? m.currency)} not paid
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DoneRow({ m, r }: { m: Model; r: Route }) {
  const p = m.person(r.riderId);
  const s = ui.get();
  return (
    <div className={`rrow${s.view === 'stop' && s.route === r.id ? ' on' : ''}`} data-ctx={`route:${r.id}`} style={rowStyle(p, s.hidden.has(r.id))} onClick={() => openRoute(r.id)}>
      <RouteHead m={m} r={r} p={p} done={r.state === 'done'} sub={<RouteMeta m={m} r={r} />} />
    </div>
  );
}

/** A route left open from an earlier day, as the phone says it: "Route still open from yesterday · 3 stops not marked". */
export function openWords(m: Model, r: Route): string {
  const left = r.stops.filter((s) => !handled(s)).length;
  return [`Route still open from ${r.startedAt != null ? dayBefore(dayOf(r.startedAt, m.zone), m.today) : 'earlier'}`, left ? `${plural(left, 'stop')} not marked` : ''].filter(Boolean).join(' · ');
}

function OffRow({ m, p }: { m: Model; p: Person }) {
  const area = p.day?.presence;
  const open = p.day?.leftOpen ?? null;
  return (
    <div className="rrow" data-ctx={`courier:${p.uid}`} style={rowStyle(p)} onClick={() => openPerson(p.uid, routeOfPerson(m))}>
      <div className="who">
        <Av p={p} faded />
        <div className="grow">
          <div className="nm off">{p.first}</div>
          <div className="meta wrap">{[open ? openWords(m, open) : 'Not out yet', area?.at ? `last seen ${dayTime(area.at, m.now, m.zone)}, rough area` : ''].filter(Boolean).join(' · ')}</div>
        </div>
        <More menu={`courier:${p.uid}`} />
      </div>
    </div>
  );
}

function JustNow({ m, closed }: { m: Model; closed: boolean }) {
  const s = ui.get();
  const ev = m.stops
    .filter((x) => handled(x) && x.at != null)
    .sort((a, b) => (b.at ?? 0) - (a.at ?? 0))
    .slice(0, 5);
  if (!ev.length) return null;
  return (
    <>
      <Sec id="now" label="Just now" closed={closed} />
      {!closed && (
        <div className="rows">
          {ev.map((x) => {
            const name = m.person(x.riderId)?.first ?? 'A courier';
            const moved = x.reason === 'anotherDay';
            const who = x.name ? firstOf(x.name) : null;
            const [icon, cls, what] =
              x.state === 'done'
                ? ['checkCircle', 'pos', x.type === 'pickup' ? `${name} picked up${who ? ` from ${who}` : ''}` : `${name} delivered${who ? ` to ${who}` : ''}`]
                : moved
                  ? ['calendarBlank', 'cau', `${who ?? 'The customer'} asked for another day`]
                  : ['xCircle', 'neg', `${name}: not delivered${who ? ` to ${who}` : ''}`];
            return (
              <div
                key={keyOf(x)}
                className={`item ev${s.view === 'stop' && s.stop === keyOf(x) ? ' hot' : ''}`}
                data-ctx={`stop:${keyOf(x)}`}
                tabIndex={0}
                role="button"
                onClick={() => openStop(keyOf(x), 'day')}
                onKeyDown={(e) => e.key === 'Enter' && openStop(keyOf(x), 'day')}
              >
                <span className={cls} style={{ marginTop: 1 }}>
                  <Icon name={icon} />
                </span>
                <div className="grow">
                  <div>{what}</div>
                  <div className="cap ellipsis">
                    {streetOf(x)}
                    {x.state === 'failed' && !moved ? ` · ${reasonWords(x.reason)}` : ''} · {time(x.at, m.zone)} · {ago(x.at!, m.now)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function DayView({ m }: { m: Model }) {
  const s = useStore(ui);
  const [lookOpen, setLookOpen] = useState<string | null>(null);
  const f = dayFacts(m);
  const waiting = m.routes.filter((r) => r.state === 'waiting' || r.state === 'declined');
  const on = m.people.filter((p) => p.day?.running);
  // Finished today by the courier's day, so a route that started before midnight and ended after it is here too.
  const finished = [...m.routes.filter((r) => r.state === 'done' || r.state === 'ended'), ...m.people.flatMap((p) => p.day?.finished ?? []).filter((r) => !m.routes.includes(r))];
  // Not out yet: no route running, waiting or finished today (the user's split, as Couriers has it).
  const idle = m.people.filter((p) => !m.routes.some((r) => r.riderId === p.uid) && !p.day?.finished.length);
  const shut = (id: string) => s.closed.has(id);
  if (!m.people.length) {
    return (
      <div className="lhead">
        <DayHead m={m} />
        <EmptyHelp
          title="No couriers in your business yet."
          tries={[
            { icon: 'userPlus', label: 'Invite a courier', note: 'A code they type in the Routr app, made on your phone for now', act: 'invite' },
            { icon: 'users', label: 'The Couriers page', note: 'Who’s in your business', act: 'couriers' },
          ]}
          onTry={(a) => (a === 'couriers' ? go('couriers') : toast('Invite couriers from the Routr app for now: Couriers, then Invite'))}
        />
      </div>
    );
  }
  const acts = m.looks.filter((x) => x.act);
  const watch = m.looks.filter((x) => !x.act);
  const line = (x: Look) => <LookLine key={x.id} x={x} open={lookOpen === x.id} onOpen={setLookOpen} />;
  return (
    <>
      <div className="lhead">
        <DayHead m={m} />
      </div>
      {(f.holds.length > 0 || f.owed.length > 0) && (
        <button className="fold" data-tip={f.moneyLine} onClick={f.seeMoney}>
          <Icon name="money" />
          <span className="grow">
            <b>{f.moneyTitle}</b>
            <span className="cap ellipsis">{f.moneyLine}</span>
          </span>
          <Icon name="caretRight" size="sm" />
        </button>
      )}
      {m.looks.length > 0 && (
        <>
          <Sec id="looks" label="Needs a look" n={m.looks.length} closed={shut('looks')} />
          {!shut('looks') && (
            <div className="rows">
              {acts.map(line)}
              {watch.length > 0 && (
                <button className="more-looks" aria-expanded={!shut('watch')} onClick={() => toggleSec('watch')}>
                  <Icon name={shut('watch') ? 'caretDown' : 'caretUp'} size="sm" />
                  {acts.length ? `${watch.length} more to watch` : `${watch.length} to watch`}
                </button>
              )}
              {!shut('watch') && watch.map(line)}
            </div>
          )}
        </>
      )}
      {on.length > 0 && (
        <>
          <Sec id="on" label="On a route" n={on.length} eye={eyeOf(on.map((p) => p.day!.running!.id), s.hidden)} closed={shut('on')} />
          {!shut('on') && (
            <div className="rows">
              {on.map((p) => (
                <RunRow key={p.uid} m={m} p={p} />
              ))}
            </div>
          )}
        </>
      )}
      {waiting.length > 0 && (
        <>
          <Sec id="waiting" label="Waiting to start" n={waiting.length} eye={eyeOf(waiting.map((r) => r.id), s.hidden)} closed={shut('waiting')} />
          {!shut('waiting') && (
            <div className="rows">
              {waiting.map((r) => (
                <SentRow key={r.id} m={m} r={r} />
              ))}
            </div>
          )}
        </>
      )}
      {finished.length > 0 && (
        <>
          <Sec id="finished" label="Finished" n={finished.length} eye={eyeOf(finished.map((r) => r.id), s.hidden)} closed={shut('finished')} />
          {!shut('finished') && (
            <div className="rows">
              {finished.map((r) => (
                <DoneRow key={r.id} m={m} r={r} />
              ))}
            </div>
          )}
        </>
      )}
      {idle.length > 0 && (
        <>
          <Sec id="idle" label="Not out yet" n={idle.length} closed={shut('idle')} />
          {!shut('idle') && (
            <div className="rows">
              {idle.map((p) => (
                <OffRow key={p.uid} m={m} p={p} />
              ))}
            </div>
          )}
        </>
      )}
      <JustNow m={m} closed={shut('now')} />
      <div style={{ height: 28 }} />
    </>
  );
}

// ── A route ───────────────────────────────────────────────────────────────

export function routeLine(m: Model, r: Route): string {
  const p = m.person(r.riderId);
  if (r.state === 'running') {
    const cur = nextStop(r);
    const finish = p?.day?.running?.id === r.id ? p.day.finishAt : null;
    return [cur ? `Stop ${cur.n} of ${r.stops.length}` : r.betweenRuns ? 'Back to load the next run' : r.returning ? 'On the way back' : `${r.stops.filter(handled).length} of ${r.stops.length} handled`, finish ? `done about ${time(finish, m.zone)}` : null, r.km ? length(r.km * 1000) : null]
      .filter(Boolean)
      .join(' · ');
  }
  if (r.state === 'waiting' || r.state === 'declined') return sentLine(m, r);
  const ended = r.completedAt ?? r.endedAt;
  return [r.state === 'ended' ? `Ended ${time(ended, m.zone)}` : `Finished ${time(ended, m.zone)}`, `${delivered(r)} of ${r.stops.length} delivered`, r.km ? length(r.km * 1000) : null].filter(Boolean).join(' · ');
}

export function StopRow({ m, s, filtered = false }: { m: Model; s: Stop; filtered?: boolean }) {
  const p = m.person(s.riderId);
  const st = ui.get();
  const facts = <Facts s={s} m={m} />;
  // A route waiting to start can be put in a new order by dragging (a started one is the courier's, a declined one goes
  // to someone else); not while "Find a stop" shows only some of its stops, where a place in the list isn't one in the route.
  const r = m.route(s.routeId);
  const draggable = r?.state === 'waiting' && !filtered;
  return (
    <div
      className={`stop ${s.state === 'left' ? 'failed' : s.state}${st.view === 'stop' && st.stop === keyOf(s) ? ' picked' : ''}`}
      style={{ '--c': p?.hex ?? '#5E594F', '--on': p?.on ?? '#fff' } as React.CSSProperties}
      data-ctx={`stop:${keyOf(s)}`}
      data-drag={draggable ? `${s.routeId}|${s.id}` : undefined}
      onClick={() => openStop(keyOf(s))}
    >
      <span className="n">{two(s.n)}</span>
      <div style={{ minWidth: 0 }}>
        <div className="a ellipsis">{streetOf(s)}</div>
        <div className="s ellipsis">{[s.area, s.name].filter(Boolean).join(' · ') || ' '}</div>
      </div>
      <TPill s={s} zone={m.zone} short />
      {(s.notes || s.access) && (
        <div className="x">
          <Icon name="notePencil" size="sm" />
          <span className="ellipsis">{[s.access, s.notes].filter(Boolean).join(' · ')}</span>
        </div>
      )}
      {facts && <div className="chips">{facts}</div>}
      <More menu={`stop:${keyOf(s)}`} />
    </div>
  );
}

function RouteView({ m, r }: { m: Model; r: Route }) {
  const s = useStore(ui);
  const p = m.person(r.riderId);
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const shown = r.stops.filter((x) => (!s.leftOnly || !handled(x)) && (!ql || [x.address, x.area, x.name, x.notes].join(' ').toLowerCase().includes(ql)));
  // A long route opens at the stop being driven to; the stop picked stays in view.
  useEffect(() => {
    document.querySelector('.stops .stop.current')?.scrollIntoView({ block: 'center' });
  }, [r.id]);
  useEffect(() => {
    if (s.view === 'stop') document.querySelector('.stops .stop.picked')?.scrollIntoView({ block: 'nearest' });
  }, [s.view, s.stop]);
  return (
    <>
      <div className="lhead routehead" style={{ '--c': p?.hex ?? '#5E594F' } as React.CSSProperties}>
        <div className="row">
          <button className="iconbtn sm" data-tip={backTip()} aria-label="Back" onClick={backToDay}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <Av p={p} />
          <div className="grow">
            <button className="nm ellipsis" data-tip={`${p?.first ?? 'Their'}’s record`} style={{ display: 'block', maxWidth: '100%', textAlign: 'left' }} onClick={() => p && openCourier(p.uid)}>
              {p?.first ?? 'A courier'} <span className="rt">· {routeTitle(r, m.zone)}</span>
            </button>
            {r.state === 'waiting' || r.state === 'declined' ? <div className="meta wrap">{sentLine(m, r)}</div> : <RouteMeta m={m} r={r} />}
          </div>
          <button className="iconbtn sm" data-tip="Print the route sheet" aria-label="Print the route sheet" onClick={() => printRoute(r.id)}>
            <Icon name="printer" />
          </button>
          <button className="iconbtn sm" data-menu={`route:${r.id}`} data-tip="Route actions" aria-label="Route actions">
            <Icon name="dotsThreeVertical" />
          </button>
        </div>
        {r.state === 'waiting' && (
          <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
            <span className="chip cau">
              <Icon name="circleDashed" size="sm" />
              {m.sending.has(r.id) ? 'Sending' : `Waiting for ${p?.first ?? 'them'} to start it`}
            </span>
            {r.stops.length > 1 && !m.sending.has(r.id) && (
              <button className="mini" data-tip="Google chooses the order again; First and Last stay, a pickup stays before its delivery" onClick={() => void act.sortSent(r.id)}>
                <Icon name="path" size="sm" />
                Sort again
              </button>
            )}
            {r.stops.length > 1 && !m.sending.has(r.id) && (
              <button className="mini" data-tip="The same stops the other way round, timed again" onClick={() => act.reverse(r.id)}>
                <Icon name="arrowsDownUp" size="sm" />
                Reverse
              </button>
            )}
            {m.ifNow.routes.get(r.id) && (
              <span className="cap" data-tip={`An estimate: ${m.ifNow.routes.get(r.id)!.google ? 'Google’s times' : 'straight lines at 30 km/h'}, and the courier’s time at each stop`}>
                If it leaves now: done about {time(m.ifNow.routes.get(r.id)!.finish, m.zone)}{m.ifNow.routes.get(r.id)!.back ? ' (back at the start)' : ''} · {length(m.ifNow.routes.get(r.id)!.distanceM)} · {inMinutes(m.ifNow.routes.get(r.id)!.durationS * 1000)}
              </span>
            )}
            {r.sentAt != null && (
              <span className="cap" data-tip="A route not started goes two days after it’s sent">
                Clears {dayWords(dayOf(r.sentAt + 2 * 86400000, m.zone))} if not started
              </span>
            )}
          </div>
        )}
        {r.state === 'declined' && (
          <div className="row" style={{ marginTop: 10, gap: 8 }}>
            <span className="chip neg">
              <Icon name="warningCircle" size="sm" />
              {p?.first ?? 'They'} can’t take it
            </span>
          </div>
        )}
        <div className="row" style={{ marginTop: 10, gap: 6 }}>
          <div className="field grow">
            <Icon name="magnifyingGlass" />
            <input
              placeholder="Find a stop"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && shown.length === 1) {
                  setQ('');
                  openStop(keyOf(shown[0]));
                }
                if (e.key === 'Escape') {
                  if (q) {
                    e.stopPropagation();
                    setQ('');
                  }
                }
              }}
              autoComplete="off"
              spellCheck={false}
              aria-label="Find a stop"
              id="routeq"
            />
          </div>
          <button
            className="ctl sq"
            data-tip={s.leftOnly ? 'Showing only what’s left · show every stop' : 'Show only what’s left'}
            aria-label="Show only what’s left"
            aria-pressed={s.leftOnly}
            onClick={() => ui.set({ leftOnly: !s.leftOnly })}
          >
            <Icon name={s.leftOnly ? 'funnelSimple' : 'list'} />
          </button>
        </div>
      </div>
      {ql && !shown.length ? (
        <EmptyHelp
          q={q.trim()}
          tries={[
            ...nearly(ql, r.stops, (x) => `${x.address} ${x.name ?? ''}`).map((x) => ({ icon: 'mapPin', label: streetOf(x), note: `Stop ${x.n}${x.name ? ` · ${x.name}` : ''}`, act: `stop:${keyOf(x)}` })),
            { icon: 'magnifyingGlass', label: 'Search every stop today', note: 'Every courier’s', act: 'allstops' },
            { icon: 'x', label: 'Clear the search', act: 'clear' },
          ]}
          onTry={(a) => {
            if (a.startsWith('stop:')) {
              setQ('');
              openStop(a.slice(5));
            } else if (a === 'allstops') {
              go('stops');
              ui.set({ q: q.trim(), tab: 'all', courier: null, sel: new Set() });
            } else setQ('');
          }}
        />
      ) : null}
      {shown.length > 0 && (
        <div className="stops">
          {shown.map((x) => (
            <StopRow key={x.id} m={m} s={x} filtered={!!ql} />
          ))}
        </div>
      )}
      {s.leftOnly && !shown.length && !ql && <div className="empty cap">Nothing left on this route: every stop has been handled.</div>}
      {r.state === 'waiting' && !ql && r.stops.length > 1 && (
        <div className="cap" style={{ padding: '0 16px 20px' }}>
          Drag a stop to change the order. {p?.first ?? 'The courier'} sees the new order on their map.
        </div>
      )}
      {r.state !== 'waiting' && leftOn(r) === 0 && r.state === 'running' && <div className="cap" style={{ padding: '0 16px 24px' }}>Every stop handled{r.returning ? '; on the way back' : ''}.</div>}
    </>
  );
}

// ── A stop's record, between the list and the map ─────────────────────────

function StopPane({ m, s }: { m: Model; s: Stop }) {
  const r = m.route(s.routeId)!;
  const i = r.stops.findIndex((x) => x.id === s.id);
  const to = (d: number) => {
    const x = r.stops[i + d];
    return x ? () => openStop(keyOf(x)) : undefined;
  };
  return <StopRecord m={m} s={s} close={backFromStop} steps={{ prev: to(-1), next: to(1) }} />;
}

// ── A courier with no route today ─────────────────────────────────────────

export function PersonView({ m, p }: { m: Model; p: Person }) {
  const area = p.day?.presence;
  return (
    <>
      <div className="lhead">
        <div className="row">
          <button className="iconbtn sm" data-tip={backTip()} aria-label="Back" onClick={backToDay}>
            <Icon name="arrowLeft" size="lg" />
          </button>
          <Av p={p} size="lg" />
          <div className="grow">
            <button className="title" data-tip={`${p.first}’s record`} onClick={() => openCourier(p.uid)}>
              {p.name}
            </button>
            <div className="cap">
              {vehicleWords(p.courier.vehicle)} · {p.onCall ? 'on call' : 'employed'}
            </div>
          </div>
          <button className="iconbtn sm" data-menu={`courier:${p.uid}`} data-tip="Courier actions" aria-label="Courier actions">
            <Icon name="dotsThreeVertical" />
          </button>
        </div>
      </div>
      <div className="rec-b" style={{ paddingTop: 4 }}>
        <b>{p.first} isn’t out yet.</b>
        {area?.at && (
          <div className="cap" style={{ marginTop: 4 }}>
            Last seen {dayTime(area.at, m.now, m.zone)}, a rough area, while Routr was open.
          </div>
        )}
        <div className="row" style={{ marginTop: 14, gap: 8, flexWrap: 'wrap' }}>
          <button className="btn strong" onClick={() => planWith([], p.uid)}>
            <Icon name="path" />
            Plan a route for {p.first}
          </button>
          {p.courier.phone && (
            <a className="btn" href={`tel:${p.courier.phone}`}>
              <Icon name="phone" />
              Call {p.first}
            </a>
          )}
        </div>
      </div>
    </>
  );
}

export { VEHICLES, vehicleWords } from '../app/words';

export { courierFirst };
